import { useState } from "react";
import { FaArrowRight, FaStar } from "react-icons/fa6";
import fallbackTherapistImage from "assets/images/therapist-fallback.png";

const getTherapistTags = (specialities = []) =>
  specialities
    .map((speciality) => speciality.problemCategory || speciality.problemID)
    .filter(Boolean);

const getPricingText = (pricing) => {
  if (!pricing) {
    return "₹--";
  }

  const price = pricing.pricePerSession || pricing.discountedPrice || pricing.basePrice;
  return price ? `₹${Number(price).toLocaleString("en-IN")}` : "₹--";
};

const getBasePriceText = (pricing) => {
  if (!pricing?.basePrice) {
    return "";
  }

  return `₹${Number(pricing.basePrice).toLocaleString("en-IN")}`;
};

const getAvailabilityLabel = (availability = {}) => {
  if (availability.availableToday) {
    return "Available Today";
  }

  if (availability.availableTomorrow && availability.availableTime) {
    return `Available Tomorrow (${availability.availableTime})`;
  }

  if (availability.availableDate && availability.availableTime) {
    return `Available At: ${availability.availableDate} (${availability.availableTime})`;
  }

  return "Slots unavailable";
};

const getLanguagesLabel = (languages = []) => {
  if (languages.length === 0) {
    return "";
  }

  if (languages.length === 1) {
    return languages[0];
  }

  if (languages.length === 2) {
    return `${languages[0]} & ${languages[1]}`;
  }

  if (languages.length === 3) {
    return `${languages[0]}, ${languages[1]} & ${languages[2]}`;
  }

  return `${languages[0]}, ${languages[1]}, ${languages[2]} & (+${languages.length - 3})`;
};

const AvailabilityContent = ({ availability, availabilityLoading }) => (
  <p className="marketplace-availability">
    <span aria-hidden="true" />
    {availabilityLoading ? (
      <span className="marketplace-availability-skeleton" aria-hidden="true" />
    ) : (
      getAvailabilityLabel(availability)
    )}
  </p>
);

const PractitionerCard = ({ practitioner, onBookNow, registerCardRef }) => {
  const tags = getTherapistTags(practitioner.specialities);
  const [showAllSpecialities, setShowAllSpecialities] = useState(false);
  const badge = practitioner.topRated
    ? "Top Rated"
    : practitioner.mostBooked
      ? "Most Booked"
      : "";
  const imageUrl = practitioner.therapistImageUrl || fallbackTherapistImage;
  const rating = practitioner.therapistRating || 0;
  const ratingCount = practitioner.therapistRatingsCount || 0;
  const visibleTags = showAllSpecialities ? tags : tags.slice(0, 3);
  const hasMoreThanThreeSpecialities = tags.length > 3;

  return (
    <article
      className="marketplace-practitioner-card"
      ref={(node) => registerCardRef?.(practitioner._id, node)}
    >
      <div className="marketplace-practitioner-media">
        {badge && (
          <span className="marketplace-practitioner-badge">
            {badge}
          </span>
        )}
        <img src={imageUrl} alt={practitioner.therapistName} />
        <div className="marketplace-rating-pill">
          <FaStar aria-hidden="true" />
          {rating || "New"} ({ratingCount})
        </div>
      </div>

      <div className="marketplace-practitioner-info">
        <div className="marketplace-practitioner-heading">
          <div>
            <h3>{practitioner.therapistName}</h3>
            <p>
              {practitioner.designation || "Therapist"} |{" "}
              {practitioner.experienceInYears || 0} Yrs
            </p>
          </div>
          <div className="marketplace-rate">
            <div className="marketplace-current-price">
              <span className="marketplace-current-price-value">
                {getPricingText(practitioner.pricing)}
              </span>
              <span className="marketplace-rate-unit">/1hr</span>
            </div>
            {practitioner.pricing?.basePrice ? (
              <span className="marketplace-base-price">
                {getBasePriceText(practitioner.pricing)}/1hr
              </span>
            ) : null}
          </div>
        </div>

        <AvailabilityContent
          availability={practitioner.availability}
          availabilityLoading={practitioner.availabilityLoading}
        />

        <div className="marketplace-card-tags">
          {visibleTags.map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
        </div>

        {hasMoreThanThreeSpecialities && (
          <button
            type="button"
            className="marketplace-speciality-toggle"
            onClick={() => setShowAllSpecialities((current) => !current)}
          >
            {showAllSpecialities ? "See less" : "See more"}
          </button>
        )}

        {(practitioner.languages || []).length > 0 && (
          <p className="marketplace-language">
            Fluent in {getLanguagesLabel(practitioner.languages)}
          </p>
        )}

        <button
          type="button"
          className="marketplace-book-button"
          onClick={() => onBookNow?.(practitioner)}
        >
          Book Now
          <FaArrowRight aria-hidden="true" />
        </button>
      </div>
    </article>
  );
};

export default PractitionerCard;
